<template>
  <Badge :variant="labelColorCoding" :size="small ? 'small' : 'medium'" :label="label" :dot="dot" />
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { BadgeVariant, Badge } from '@tallo/design-system';
import { RentStage } from '@tallo/property';

const props = defineProps<{
  status: RentStage;
  small?: boolean;
  dot?: boolean;
}>();

// Records, not Maps: a stage added to the enum without copy here is a compile error rather than a
// silently empty badge.
const statusColorMap: Record<RentStage, BadgeVariant> = {
  [RentStage.INITIAL_CONTACT]: 'orange',
  [RentStage.REQUIREMENTS_NOT_MET]: 'red',
  [RentStage.SHOWING_REQUESTED]: 'blue',
  [RentStage.SHOWING_CONFIRMED]: 'green',
  [RentStage.SHOWING_RESCHEDULE_REQUESTED_BY_RENTER]: 'yellow',
  [RentStage.SHOWING_RESCHEDULE_REQUESTED_BY_OWNER]: 'yellow',
  [RentStage.SHOWING_DECLINED]: 'red',
  [RentStage.SHOWING_COMPLETED]: 'green',
  [RentStage.SHOWING_CANCELLED_BY_RENTER]: 'grey',
  [RentStage.SHOWING_CANCELLED_BY_OWNER]: 'grey',
  [RentStage.IDENTITY_VERIFICATION_FAILED]: 'red',
  [RentStage.STOPPED_PROPERTY_RENTED_OUT]: 'green',
  [RentStage.SHOWING_REQUEST_IGNORED_BY_OWNER]: 'grey',
  [RentStage.APPLICATION_INVITE_SENT]: 'yellow',
  [RentStage.APPLICATION_IN_PROGRESS]: 'orange',
  [RentStage.APPLICATION_COMPLETED]: 'blue',
  [RentStage.APPLICATION_ACCEPTED]: 'green',
  [RentStage.APPLICATION_REJECTED]: 'red',
  [RentStage.NO_LONGER_INTERESTED]: 'grey',
  [RentStage.WENT_SILENT]: 'grey',
};

const labelMap: Record<RentStage, string> = {
  [RentStage.INITIAL_CONTACT]: 'Initial contact',
  [RentStage.SHOWING_REQUESTED]: 'Showing requested',
  [RentStage.REQUIREMENTS_NOT_MET]: 'Not qualified',
  [RentStage.SHOWING_CONFIRMED]: 'Showing confirmed',
  [RentStage.SHOWING_RESCHEDULE_REQUESTED_BY_RENTER]: 'Renter requested showing reschedule',
  [RentStage.SHOWING_RESCHEDULE_REQUESTED_BY_OWNER]: 'You requested showing reschedule',
  [RentStage.SHOWING_DECLINED]: 'Showing declined',
  [RentStage.SHOWING_COMPLETED]: 'Showing completed',
  [RentStage.SHOWING_CANCELLED_BY_RENTER]: 'Renter cancelled the showing',
  [RentStage.SHOWING_CANCELLED_BY_OWNER]: 'You cancelled the showing',
  [RentStage.IDENTITY_VERIFICATION_FAILED]: 'ID verification failed',
  [RentStage.STOPPED_PROPERTY_RENTED_OUT]: 'Stopped ∙ Property rented out',
  [RentStage.SHOWING_REQUEST_IGNORED_BY_OWNER]: 'You ignored the showing',
  [RentStage.APPLICATION_INVITE_SENT]: 'Application invite sent',
  [RentStage.APPLICATION_IN_PROGRESS]: 'Application In Progress',
  [RentStage.APPLICATION_COMPLETED]: 'Application Completed',
  [RentStage.APPLICATION_ACCEPTED]: 'Application Accepted',
  [RentStage.APPLICATION_REJECTED]: 'Application Rejected',
  [RentStage.NO_LONGER_INTERESTED]: 'No longer interested',
  [RentStage.WENT_SILENT]: 'Went silent',
};

const labelColorCoding = computed(() => statusColorMap[props.status]);
const label = computed(() => labelMap[props.status]);
</script>
